import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices/DomainServicesContext";
import {
  createSessionWithFiles,
  getSessionCompositionCounts,
  type CreateSessionRequest,
  type SessionCompositionEntry,
  type SessionCompositionOptions,
  type SessionCompositionOutcome,
  type SessionCreationResult,
} from "@/shared/domain/sessions";
import { shouldReportError } from "@/shared/http/shouldReportError";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { sessionQueryKeys } from "./sessionQueryKeys";

export interface UseCreateSessionFromFilesVariables {
  request: CreateSessionRequest;
  outcome: SessionCompositionOutcome;
  options?: SessionCompositionOptions;
}

/**
 * Create a session holding a prepared selection of files, and report what the create, the file adds,
 * and any rollback delete did. A server refusal or a lost answer is a returned
 * `SessionCreationResult`, never a rejection. The mutation rejects before sending anything for a
 * selection already submitted or a stop requested before submission, and at any point when the
 * caller's `stopRequested` or `onProgress` throws, after trying to delete any session it created.
 *
 * Cache effects on every settle, rejection included:
 * 1. `lists()` is always invalidated. A create whose reply was lost may still have produced a session
 *    this client cannot name, and an extra list refetch is cheaper than a session missing from the list.
 * 2. Once a session id is known, `detail(sessionId)`, the prefix of the session's files and topics
 *    reads, is invalidated, except after a rollback delete the server confirmed, where those entries
 *    are removed instead: the session is gone, so there is nothing to refetch.
 */
export function useCreateSessionFromFiles() {
  const { sessionService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<
    SessionCreationResult,
    Error,
    UseCreateSessionFromFilesVariables
  >({
    mutationFn: async ({ request, outcome, options }) => {
      let latestOutcome = outcome;
      let result: SessionCreationResult | undefined;
      try {
        result = await createSessionWithFiles(
          sessionService,
          request,
          outcome,
          {
            ...options,
            onProgress: (progress) => {
              latestOutcome = progress;
              options?.onProgress?.(progress);
            },
          },
        );
        latestOutcome = result.outcome;
        reportCreationErrors(result);
        return result;
      } finally {
        const refreshes = [
          queryClient.invalidateQueries({ queryKey: sessionQueryKeys.lists() }),
        ];
        // `latestOutcome` carries the session id even when `createSessionWithFiles` threw, because the
        // progress observer records each outcome before handing it to the caller's own observer.
        const sessionId = latestOutcome.sessionId;
        if (sessionId !== undefined) {
          if (result?.kind === "compensated") {
            queryClient.removeQueries({
              queryKey: sessionQueryKeys.detail(sessionId),
            });
          } else {
            refreshes.push(
              queryClient.invalidateQueries({
                queryKey: sessionQueryKeys.detail(sessionId),
              }),
            );
          }
        }
        await Promise.allSettled(refreshes);
      }
    },
  });
}

/**
 * Send whichever of the call's two errors are worth a Sentry issue: the one that cost the user the
 * session, and the rollback delete that could not clean up after it.
 *
 * Each report is guarded on its own, so monitoring failing on the first still reports the second, and
 * neither can turn a settled call into a rejected mutation.
 */
function reportCreationErrors(result: SessionCreationResult) {
  const { outcome } = result;
  const cause = outcome.stopCause;
  let primaryError: unknown;
  if (result.kind === "creationUnknown") {
    primaryError = result.error;
  } else if (cause && cause.kind !== "stopped") {
    primaryError = cause.error;
  }
  const context = {
    operationId: outcome.context.operationId,
    orgId: outcome.context.orgId,
    sessionId: outcome.sessionId,
    counts: getSessionCompositionCounts(outcome),
  };
  if (primaryError !== undefined && shouldReportError(primaryError)) {
    try {
      ErrorMonitoringService.captureError(primaryError, context);
    } catch {
      // Monitoring being down must not turn a settled call into a rejected mutation.
    }
  }
  if (
    result.kind !== "cleanupUnknown" ||
    !shouldReportError(result.cleanupError)
  ) {
    return;
  }
  // A refusal stops nothing, so it leaves no `stopCause`; it is still what triggered the
  // rollback and the context a triager needs to read the delete failure against.
  const refusal = outcome.entries.find(
    (entry): entry is Extract<SessionCompositionEntry, { status: "refused" }> =>
      entry.status === "refused",
  );
  try {
    ErrorMonitoringService.captureError(
      result.cleanupError,
      {
        ...context,
        primaryError: primaryError ?? refusal?.error,
        stopCause: cause,
      },
      // A 403 or 404 on the delete is not bad user input: it means a session holding only some of the
      // selected files may still exist.
      { reportHandledClientError: true },
    );
  } catch {
    // Monitoring being down must not turn a settled call into a rejected mutation.
  }
}
